import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

interface CategoryCount {
  category: string;
  count: number;
}

const categoryIcons: Record<string, string> = {
  'Real Estate': 'ri-home-4-line',
  'Vehicles': 'ri-car-line',
  'Electronics': 'ri-smartphone-line',
  'Services': 'ri-tools-line',
  'Jobs': 'ri-briefcase-line',
  'Fashion': 'ri-shirt-line',
  'Furniture': 'ri-sofa-line',
  'Sports': 'ri-football-line',
};

export default function CategoriesSection() {
  const [categories, setCategories] = useState<CategoryCount[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('ads')
        .select('category')
        .eq('status', 'active');

      if (error) throw error;

      // Count ads per category
      const categoryCounts: Record<string, number> = {};
      data?.forEach(ad => {
        categoryCounts[ad.category] = (categoryCounts[ad.category] || 0) + 1;
      });

      // Convert to array and sort by count
      const categoryArray = Object.entries(categoryCounts)
        .map(([category, count]) => ({ category, count }))
        .sort((a, b) => b.count - a.count);

      setCategories(categoryArray);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const getIconForCategory = (category: string): string => {
    return categoryIcons[category] || 'ri-price-tag-3-line';
  };

  if (loading) {
    return (
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Browse by Category</h2>
            <p className="text-gray-600">Loading categories...</p>
          </div>
        </div>
      </section>
    );
  }

  if (categories.length === 0) {
    return (
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Browse by Category</h2>
            <p className="text-gray-600">No categories available yet. Start posting ads!</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-3">Browse by Category</h2>
          <p className="text-gray-600">Find what you need in your preferred category</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {categories.map((item) => (
            <a
              key={item.category}
              href="/categories"
              className="bg-white rounded-lg p-6 border border-gray-200 hover:border-teal-500 hover:shadow-md transition-all duration-300 cursor-pointer group"
            >
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 bg-teal-50 rounded-full flex items-center justify-center mb-4 group-hover:bg-teal-100 transition-colors">
                  <i className={`${getIconForCategory(item.category)} text-3xl text-teal-600`}></i>
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">{item.category}</h3>
                <p className="text-sm text-gray-500">{item.count.toLocaleString()} ads</p>
              </div>
            </a>
          ))}
        </div>

        <div className="text-center mt-10">
          <a
            href="/categories"
            className="inline-flex items-center gap-2 text-teal-600 font-semibold hover:text-teal-700 transition-colors cursor-pointer"
          >
            View All Categories
            <i className="ri-arrow-right-line"></i>
          </a>
        </div>
      </div>
    </section>
  );
}
